import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { faArrowsRotate } from '@fortawesome/pro-light-svg-icons/faArrowsRotate';
import { FYMAPIError } from 'fym-common/src/generics/errors/FYMAPIError';
import {
  PRESAVE_FAN_RELEASE_MAX_AGE_DAYS,
  PresaveFanReleaseState,
} from 'fym-shared/src/smartlinks/presaveSubscriptions';
import { TypedMemo } from '../../../../common/types';
import { errorAlertMessage } from '../../../../common/views/ErrorAlert/errorAlertMessage';
import { useSendFanReleaseMutation } from '../../../../../redux/api/fymAPI';
import { useSMLRouter } from '../../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../../routing/sml/SMLRoutes';
import { SMLButton } from '../../../components/primitives/SMLButton';
import { SMLUpgradeFeature } from '../../../config/music24PlanCapabilities';
import { useUpgradePrompt } from '../../../hooks/useUpgradePrompt';
import { demoReadOnlyMessage, parseM24LimitError } from '../../../utils/parseM24LimitError';
import { usePresaveLockLabel } from '../../../../smartLinks/presave/usePresaveLockLabel';
import { FanReleasePlanState } from './FanReleasePlanState';
import { FanReleaseCard } from './FanReleaseCard';
import { fanReleaseMessages as messages } from './overviewMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole card; each part uses its own slice of it */
import styles from './FanReleasePanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface FanReleasePanelProps {
  accountId: number;
  catalogItemUuid: string;
  title: string;
  fanRelease: PresaveFanReleaseState | null;
  /** "Check again" of a song not on the services yet: the Overview loads again. */
  onCheckAgain?: () => void;
}

/**
 * A plan limit (402) opens the global prompt and the demo artist (403) shows its toast, both through
 * `customBaseQuery`. A 409 reloads the Overview into the sent state. None needs a line of its own.
 */
function shownError(error: unknown): boolean {
  if (parseM24LimitError(error) !== null || demoReadOnlyMessage(error) !== null) {
    return false;
  }
  return !(error instanceof FYMAPIError && error.statusCode === 409);
}

/**
 * "Send to your subscribers" (design presave-subscriptions d2): a song released with no pre-save
 * goes to the fans who subscribed to the artist, one time. Hidden with no subscriber, before the
 * release and for a song with a campaign (the campaign sends on its own).
 */
const FanReleasePanelComponent: React.FC<FanReleasePanelProps> = ({
  accountId,
  catalogItemUuid,
  title,
  fanRelease,
  onCheckAgain,
}) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const openUpgrade = useUpgradePrompt();
  const lockLabel = usePresaveLockLabel();
  const [sendFanRelease, { isLoading, error }] = useSendFanReleaseMutation();

  const send = useCallback(() => {
    sendFanRelease({ accountId, catalogItemUuid }).catch(console.error);
  }, [accountId, catalogItemUuid, sendFanRelease]);
  const openFans = useCallback(() => router.push(SMLRoutes.fans), [router]);
  const openPlans = useCallback(() => openUpgrade(SMLUpgradeFeature.presave, title), [openUpgrade, title]);

  if (fanRelease === null || fanRelease.subscriberCount <= 0 || fanRelease.reason === 'notReleased') {
    return null;
  }
  const count = fanRelease.subscriberCount;

  if (fanRelease.releaseUuid !== null) {
    return (
      <FanReleaseCard>
        <div className={styles.okLine}>
          <span className={styles.okMark}>
            <FontAwesomeIcon icon={faCheck} />
          </span>
          {intl.formatMessage(messages.sent, { count })}
        </div>
        <button type="button" className={styles.link} onClick={openFans}>
          {intl.formatMessage(messages.seeFans)}
        </button>
      </FanReleaseCard>
    );
  }

  if (fanRelease.reason === 'tooOld') {
    return (
      <FanReleaseCard
        isOff
        title={intl.formatMessage(messages.tooOldTitle, { title, days: PRESAVE_FAN_RELEASE_MAX_AGE_DAYS })}
        body={intl.formatMessage(messages.tooOldBody)}
      />
    );
  }

  if (fanRelease.reason === 'noServiceIds') {
    return (
      <FanReleaseCard
        isOff
        title={intl.formatMessage(messages.noServiceIdsTitle, { title })}
        body={intl.formatMessage(messages.noServiceIdsBody)}
      >
        {onCheckAgain !== undefined && (
          <div className={styles.actions}>
            <SMLButton icon={<FontAwesomeIcon icon={faArrowsRotate} />} onClick={onCheckAgain}>
              {intl.formatMessage(messages.checkAgain)}
            </SMLButton>
          </div>
        )}
      </FanReleaseCard>
    );
  }

  if (fanRelease.reason === 'plan' || lockLabel !== null) {
    return <FanReleasePlanState title={title} count={count} lockLabel={lockLabel} onSeePlans={openPlans} />;
  }

  return (
    <FanReleaseCard
      title={intl.formatMessage(messages.sendTitle, { title, count })}
      body={intl.formatMessage(messages.sendBody, { title })}
    >
      <div className={styles.actions}>
        <SMLButton variant="primary" disabled={isLoading} onClick={send}>
          {isLoading ? intl.formatMessage(messages.sending) : intl.formatMessage(messages.sendButton, { count })}
        </SMLButton>
      </div>
      {error !== undefined && shownError(error) && (
        <p className={styles.error} role="alert">
          {errorAlertMessage(intl, error)}
        </p>
      )}
    </FanReleaseCard>
  );
};

export const FanReleasePanel = TypedMemo(FanReleasePanelComponent);
